import {
    afterNextRender,
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    ElementRef,
    inject,
    Injector,
    signal,
    viewChild,
} from "@angular/core";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { LoginStore } from "../../stores/login.store";
import { ContactService } from "../../services/contact.service";
import { S25Const } from "../../util/s25-const";
import { s25Resource } from "../../util/s25.resource";
import { S25LoadingInlineStaticComponent } from "../../modules/s25-loading/s25.loading.inline.static.component";
import { S25Help } from "../../modules/s25-help/s25.help.service";

type FeatureUpvoteUser = { id: string; displayName: string; email: string };
type FeatureUpvoteWidget = {
    init(options: { target: string; boardRef: string; user: FeatureUpvoteUser }): void;
};

declare global {
    interface Window {
        FeatureUpvote?: FeatureUpvoteWidget;
    }
}

// This is the unique identifier of the board, not a secret.
const BoardRef = "pr_5fizxlsyg7xpmy7";
const WidgetScriptUrl = "https://app.featureupvote.com/widget/v0/widget.js";
const BoardTargetId = "feature-upvote-board";

@TypeManagerDecorator("s25-product-feedback")
@Component({
    selector: "s25-product-feedback",
    standalone: true,
    imports: [S25LoadingInlineStaticComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        @if (portalUrl()) {
            <p class="c-margin-bottom--single">
                <a [href]="portalUrl()">Go to Feedback portal</a>
            </p>
        }
        @switch (status()) {
            @case ("loading") {
                <s25-ng-loading-inline-static role="status" text="Loading product feedback..." />
            }
            @case ("missing-email") {
                <p class="product-feedback-message">Add a work email to your contact record to use product feedback.</p>
            }
            @case ("error") {
                <p class="product-feedback-message" role="alert">Product feedback is unavailable right now.</p>
                @if (portalUrl()) {
                    <p class="product-feedback-fallback">
                        You can still submit feedback on the
                        <a [href]="portalUrl()">feedback portal</a>.
                    </p>
                }
            }
        }
        @if (voter()) {
            <iframe #frame title="Product feedback" [hidden]="status() !== 'ready'" (load)="onFrameLoad()"></iframe>
        }
    `,
    styles: `
        .product-feedback-fallback a {
            text-decoration: underline;
        }

        iframe {
            width: 100%;
            height: 80vh;
            border: 0;
        }

        iframe[hidden] {
            display: none;
        }
    `,
})
export class S25ProductFeedbackComponent {
    private readonly loginStore = inject(LoginStore);
    private readonly injector = inject(Injector);
    private readonly frame = viewChild<ElementRef<HTMLIFrameElement>>("frame");
    private readonly boardError = signal(false);
    private readonly boardReady = signal(false);
    private initAttempted = false;
    /** One retry after a rejected password request. `@Cache` drops rejections, so this is a new request. */
    private readonly portalUrlRetried = signal(false);
    private readonly portalUrlResource = s25Resource({
        params: () => this.portalUrlRetried(),
        loader: () => S25Help.getFeedbackUrl(),
    });
    readonly portalUrl = computed(() => this.portalUrlResource.value() || "");

    private readonly emailResource = s25Resource(() => ContactService.getCurrentEmail());

    readonly voter = computed((): FeatureUpvoteUser | null => {
        const user = this.loginStore.user();
        const email = this.emailResource.value()?.trim();
        if (!user || !email) return null;

        return {
            id: `${S25Const.instanceId}:${user.userId}`,
            displayName: user.contactName?.trim() || "Name unavailable",
            email,
        };
    });

    readonly status = computed(() => {
        if (this.boardError() || this.emailResource.error()) {
            return "error" as const;
        }
        if (!this.loginStore.user() || this.emailResource.isLoading()) return "loading" as const;
        if (!this.voter()) return "missing-email" as const;
        if (!this.boardReady()) return "loading" as const;
        return "ready" as const;
    });

    constructor() {
        effect(() => {
            if (!this.portalUrlResource.error() || this.portalUrlRetried()) return;
            this.portalUrlRetried.set(true);
        });

        effect(() => {
            const voter = this.voter();
            if (!voter) return;
            afterNextRender(() => this.mountFrame(voter), { injector: this.injector });
        });
    }

    onFrameLoad() {
        const voter = this.voter();
        if (!voter) return;
        this.mountFrame(voter);
    }

    private mountFrame(voter: FeatureUpvoteUser) {
        if (this.initAttempted) return;
        const doc = this.frame()?.nativeElement.contentDocument;
        if (!doc?.body) return;

        if (!doc.getElementById(BoardTargetId)) {
            const target = doc.createElement("div");
            target.id = BoardTargetId;
            doc.body.appendChild(target);
        }

        // Feature Upvote sizes the board in rem against html { font-size: 10px }.
        // rem follows this document's root, including inside the widget's shadow tree.
        doc.documentElement.style.fontSize = "10px";
        this.initAttempted = true;
        void this.startWidget(doc, voter);
    }

    private async startWidget(doc: Document, voter: FeatureUpvoteUser) {
        try {
            const featureUpvote = await this.loadWidget(doc);
            featureUpvote.init({ target: BoardTargetId, boardRef: BoardRef, user: voter });
        } catch (caughtError) {
            console.error(caughtError);
            this.boardError.set(true);
        } finally {
            this.boardReady.set(true);
        }
    }

    /**
     * widget.js declares `let FeatureUpvote` in the document that loads it. That binding is not
     * a property of `window` until a classic script in the same document copies it over.
     * The script has to live in the frame: its `document` is the frame, so its rem root is too.
     */
    private loadWidget(doc: Document): Promise<FeatureUpvoteWidget> {
        if (window.unitTesting) {
            if (!window.FeatureUpvote?.init) return Promise.reject(new Error("Feature Upvote widget did not start"));
            return Promise.resolve(window.FeatureUpvote);
        }

        return new Promise((resolve, reject) => {
            const script = doc.createElement("script");
            script.src = WidgetScriptUrl;
            script.onload = () => {
                const bridge = doc.createElement("script");
                bridge.textContent = "window.FeatureUpvote = FeatureUpvote;";
                doc.head.appendChild(bridge);
                bridge.remove();

                const featureUpvote = (doc.defaultView as (Window & { FeatureUpvote?: FeatureUpvoteWidget }) | null)
                    ?.FeatureUpvote;
                if (!featureUpvote?.init) {
                    reject(new Error("Feature Upvote widget did not start"));
                    return;
                }
                resolve(featureUpvote);
            };
            script.onerror = () => reject(new Error(`Failed to load ${WidgetScriptUrl}`));
            doc.head.appendChild(script);
        });
    }
}
